export interface VideoFrameSource {
    readonly videoWidth: number;
    readonly videoHeight: number;
}

export interface FrameCanvasContext<TFrame> {
    drawImage(image: TFrame, dx: number, dy: number, dw: number, dh: number): void;
}

export interface FrameCanvas<TFrame> {
    width: number;
    height: number;
    getContext(contextId: "2d"): FrameCanvasContext<TFrame> | null;
    toBlob(callback: (blob: Blob | null) => void, type: string, quality: number): void;
}

const JPEG_MIME_TYPE = "image/jpeg";
const JPEG_QUALITY = 0.92;
const CAPTURED_PHOTO_FILENAME = "business-card.jpg";

const encodeAsJpeg = <TFrame>(canvas: FrameCanvas<TFrame>): Promise<Blob> =>
    new Promise((resolve, reject) => {
        canvas.toBlob(
            blob => {
                if (blob) {
                    resolve(blob);
                    return;
                }
                reject(new Error("Couldn't save the photo. Please try again."));
            },
            JPEG_MIME_TYPE,
            JPEG_QUALITY,
        );
    });

export const captureVideoFrame = async <TFrame extends VideoFrameSource>(
    video: TFrame,
    canvas: FrameCanvas<TFrame>,
): Promise<File> => {
    if (video.videoWidth === 0 || video.videoHeight === 0) {
        throw new Error("The camera isn't ready yet. Wait a moment and try again.");
    }
    const context = canvas.getContext("2d");
    if (!context) {
        throw new Error(
            "This browser can't capture photos. Please choose a photo from your device instead.",
        );
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
    const blob = await encodeAsJpeg(canvas);
    return new File([blob], CAPTURED_PHOTO_FILENAME, { type: JPEG_MIME_TYPE });
};
